import { useTranslation } from 'react-i18next'

import { useSidebar } from '@/components/ui/sidebar'
import {
  type DocumentPreviewSource,
  useDocumentPreview,
} from '@/contexts/document-preview-context'
import { getStorageDownloadUrl } from '@/integrations/firebase'

export const usePreviewDocument = () => {
  const { setOpen } = useSidebar()
  const { setPreviewUrl } = useDocumentPreview()

  const previewDocument = async (path: string, mimeType: string) => {
    const url = await getStorageDownloadUrl(path)
    setPreviewUrl(url, mimeType)
    setOpen(true)
  }

  return previewDocument
}

type DocumentForPreview = {
  files?: Array<{ filePath: string; mimeType: string; originalName: string; order: number }>
  // Note: combinedFilePath is internal-only on the backend response. We surface a derived
  // helper here so callers (e.g. the documents table) can opt into showing it.
}

/**
 * Multi-source preview: shows the combined PDF as the default segment (when provided)
 * plus one segment per file in order. For DOCX-only Documents pass `combinedFilePath`
 * as undefined and only file segments will be shown.
 */
export const usePreviewDocumentFiles = () => {
  const { setOpen } = useSidebar()
  const { setPreviewSources } = useDocumentPreview()
  const { t } = useTranslation()

  return async (document: DocumentForPreview, combinedFilePath?: string) => {
    const fileSegments = (document.files ?? [])
      .slice()
      .sort((a, b) => a.order - b.order)
      .map((f) => ({ label: f.originalName, path: f.filePath, mimeType: f.mimeType }))

    const segments: { label: string; path: string; mimeType: string }[] = combinedFilePath
      ? [
          {
            label: t('documentPreview.combinedLabel'),
            path: combinedFilePath,
            mimeType: 'application/pdf',
          },
          ...fileSegments,
        ]
      : fileSegments

    const sources: DocumentPreviewSource[] = await Promise.all(
      segments.map(async (s) => ({
        label: s.label,
        url: await getStorageDownloadUrl(s.path),
        mimeType: s.mimeType,
      })),
    )
    setPreviewSources(sources, 0)
    setOpen(true)
  }
}
